// /frontend/src/store/slices/authSlice.js (updated with cart reset event)
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import apiClient from '../../utils/apiClient';

// Base URL for API requests
const API_URL = `${process.env.REACT_APP_API_URL}/users`;

export const updateProfile = createAsyncThunk(
  'auth/updateProfile',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`${API_URL}/profile`, userData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update profile');
    }
  }
);

// Register user
export const register = createAsyncThunk(
  'auth/register',
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(`${API_URL}/register`, userData);
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      // Handle duplicate email error specifically
      if (error.response && error.response.data && error.response.data.error) {
        // Check if it's a duplicate key error
        if (error.response.data.error.includes('Duplicate field value entered') || 
            error.response.data.error.includes('already exists')) {
          return rejectWithValue('Email address is already registered. Please use a different email or try logging in.');
        }
        return rejectWithValue(error.response.data.error);
      }
      return rejectWithValue('Registration failed. Please try again later.');
    }
  }
);

// Login user
export const login = createAsyncThunk(
  'auth/login',
  async (userData, { rejectWithValue }) => {
    try {
      window.dispatchEvent(new CustomEvent('cart-reset'));
      
      const response = await axios.post(`${API_URL}/login`, userData);
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response && error.response.data.error
          ? error.response.data.error
          : 'Invalid credentials'
      );
    }
  }
);

// Logout user
export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await axios.get(`${API_URL}/logout`);
  } catch (error) {
    console.error('Logout error:', error);
  }
  
  // Remove token from localStorage regardless of API success
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  
  // Dispatch cart reset event
  console.log('Dispatching cart-reset event on logout');
  window.dispatchEvent(new CustomEvent('cart-reset'));
});

// Verify token and get user data
export const verifyToken = createAsyncThunk(
  'auth/verifyToken',
  async (_, { rejectWithValue }) => {
    try {
      // Get token from localStorage
      const token = localStorage.getItem('token');
      
      if (!token) {
        return rejectWithValue('No token found');
      }
      
      // Set auth header
      const config = {
        headers: {
          Authorization: `Bearer ${token}`
        }
      };
      
      const response = await axios.get(`${API_URL}/verify`, config);
      return response.data;
    } catch (error) {
      // Remove invalid token
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Dispatch cart reset event on invalid token
      console.log('Dispatching cart-reset event on token verification failure');
      window.dispatchEvent(new CustomEvent('cart-reset'));
      
      return rejectWithValue(
        error.response && error.response.data.error
          ? error.response.data.error
          : 'Session expired. Please login again.'
      );
    }
  }
);

// Get initial state from localStorage
const getUserFromStorage = () => {
  const userString = localStorage.getItem('user');
  if (userString) {
    try {
      return JSON.parse(userString);
    } catch (error) {
      localStorage.removeItem('user');
      return null;
    }
  }
  return null;
};

const initialState = {
  token: localStorage.getItem('token') || null,
  user: getUserFromStorage(),
  isAuthenticated: !!localStorage.getItem('token'),
  loading: false,
  error: null
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetAuth: () => {
      // Dispatch cart reset event when auth is manually reset
      window.dispatchEvent(new CustomEvent('cart-reset'));
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.token;
        state.user = action.payload.user;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.user = null;
        state.isAuthenticated = false;
        // Note: cart-reset event is dispatched in the logout thunk
      })
      
      // Verify token
      .addCase(verifyToken.pending, (state) => {
        state.loading = true;
      })
      .addCase(verifyToken.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
      })
      .addCase(verifyToken.rejected, (state) => {
        state.loading = false;
        state.isAuthenticated = false;
        state.token = null;
        state.user = null;
        // Note: cart-reset event is dispatched in the verifyToken thunk
      });
  }
});

export const { clearError, resetAuth } = authSlice.actions;

export default authSlice.reducer;